import { useTranslation } from 'react-i18next';
import type { UseFormReturn } from 'react-hook-form';

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/atoms/card';
import { Input } from '@/components/atoms/input';
import { Switch } from '@/components/atoms/switch';
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/molecules/form-field';
import type { ShippingRateFormData } from '../types/shipping-form.types';

interface ShippingRateDeliverySettingsProps {
  form: UseFormReturn<ShippingRateFormData>;
}

export function ShippingRateDeliverySettings({ form }: ShippingRateDeliverySettingsProps) {
  const { t } = useTranslation();
  const watchFreeShippingEnabled = form.watch('freeShippingEnabled');
  const watchDeliverySettingsEnabled = form.watch('deliverySettingsEnabled');

  return (
    <>
      {/* Free Shipping Threshold */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0">
              <CardTitle className="text-base sm:text-lg">
                {t('shipping.freeShippingThreshold', { defaultValue: 'Free Shipping Threshold' })}
              </CardTitle>
              <CardDescription>
                {t('shipping.freeShippingDesc', { defaultValue: 'Offer free shipping when order total passes a threshold.' })}
              </CardDescription>
            </div>
            <FormField
              control={form.control}
              name="freeShippingEnabled"
              render={({ field }) => (
                <FormItem className="shrink-0">
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        </CardHeader>
        {watchFreeShippingEnabled && (
          <CardContent>
            <FormField
              control={form.control}
              name="freeShippingThreshold"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('shipping.threshold', { defaultValue: 'Threshold Amount' })}</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      {...field}
                      value={field.value ?? ''}
                      onChange={(e) => field.onChange(e.target.valueAsNumber || undefined)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
        )}
      </Card>

      {/* Delivery Time Settings */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0">
              <CardTitle className="text-base sm:text-lg">
                {t('shipping.deliverySettings', { defaultValue: 'Delivery Settings' })}
              </CardTitle>
              <CardDescription>
                {t('shipping.deliverySettingsDesc', { defaultValue: 'Specify estimated delivery time customers will see.' })}
              </CardDescription>
            </div>
            <FormField
              control={form.control}
              name="deliverySettingsEnabled"
              render={({ field }) => (
                <FormItem className="shrink-0">
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        </CardHeader>
        {watchDeliverySettingsEnabled && (
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="estimatedDeliveryDays"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('shipping.minDays', { defaultValue: 'Min Days' })}</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min="1"
                        {...field}
                        value={field.value ?? ''}
                        onChange={(e) => field.onChange(e.target.valueAsNumber || undefined)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="maxDeliveryDays"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('shipping.maxDays', { defaultValue: 'Max Days' })}</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min="1"
                        {...field}
                        value={field.value ?? ''}
                        onChange={(e) => field.onChange(e.target.valueAsNumber || undefined)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </CardContent>
        )}
      </Card>
    </>
  );
}
